// pages/lab/predictions.js
// ════════════════════════════════════════════════════════════════════
// LAB — Pre-registration calendar (Phase F9)
//
// Aggregates every pre-registered prediction across the corpus into
// a single timeline-ordered view. Each prediction shows its claim,
// falsifier band, resolution date, parent finding, and current status.
//
// This is the credibility unlock — the public falsifier register.
// When a prediction's resolution date arrives, status flips from
// 'open' to 'held' / 'overturned' / 'partial' in the parent finding's
// PREDICTIONS array; the calendar reflects it automatically.
// ════════════════════════════════════════════════════════════════════

import Head from 'next/head';
import Link from 'next/link';
import {
  listPredictions,
  predictionStats,
  daysUntil,
} from '../../features/findings/predictions-registry';

const DOMAIN_LABELS = {
  strategic: 'Strategic Decisions',
  popdec:    'Population Decline',
};

const STATUS_LABELS = {
  open:       'Open',
  held:       'Held',
  overturned: 'Overturned',
  partial:    'Partial',
  retired:    'Retired',
};

const STATUS_TONES = {
  open:       'brass',
  held:       'sage',
  overturned: 'oxblood',
  partial:    'cognac',
  retired:    'neutral',
};

export async function getStaticProps() {
  const predictions = listPredictions();
  const stats = predictionStats();
  return { props: { predictions, stats } };
}

export default function PredictionsPage({ predictions, stats }) {
  return (
    <div className="sp-root">
      <Head>
        <title>Pre-registration calendar · Lab · Shift</title>
        <meta
          name="description"
          content="Public falsifier register. Every Lab finding pre-registers predictions with resolution dates. The calendar tracks them all."
        />
      </Head>

      <div className="sp-page">
        <div className="findings-index-topbar">
          <Link href="/findings" className="finding-back">← Findings index</Link>
          <div className="findings-index-topbar-meta">
            Lab · Pre-registration calendar
          </div>
        </div>

        <header className="findings-index-header">
          <div className="finding-eyebrow">§ Public falsifier register</div>
          <h1 className="findings-index-title">Predictions on the calendar.</h1>
          <p className="findings-index-sub">
            Every Lab finding pre-registers predictions with resolution
            dates and falsifier bands. When the data arrives, the prediction
            either holds, overturns, or comes back partial. No goalpost-moving
            after the fact &mdash; the original band is the band.
            New here? <Link href="/lab/methodology" className="methodology-inline-link">Read the methodology</Link> first.
          </p>
        </header>

        <section className="findings-index-stats">
          <Stat label="Total" value={stats.total} />
          <Stat label="Open" value={stats.open} />
          <Stat label="Held" value={stats.held} />
          <Stat label="Overturned" value={stats.overturned} />
        </section>

        <section className="predictions-timeline">
          {predictions.map((p) => (
            <PredictionCard key={p.id} p={p} />
          ))}
        </section>

        <footer className="findings-index-footer">
          <p>
            Pre-registration is what separates a research finding from a
            written opinion. The bands are committed before resolution data
            is available; the falsifier is what would force a retraction or
            revision. <strong>If a prediction overturns, the parent finding&rsquo;s
            lifecycle moves to <code>revised</code> or <code>retracted</code></strong>
            &mdash; visible on its versioned URL with a what-changed banner.
          </p>
        </footer>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="findings-index-stat">
      <div className="findings-index-stat-value">{value}</div>
      <div className="findings-index-stat-label">{label}</div>
    </div>
  );
}

function PredictionCard({ p }) {
  const tone = STATUS_TONES[p.status] || 'neutral';
  const days = daysUntil(p.sortKey);
  const isAntiPrediction = /anti-prediction/i.test(p.label);

  return (
    <article className={`prediction-card prediction-card-${tone}`}>
      <div className="prediction-card-head">
        <div className="prediction-card-domain">
          {DOMAIN_LABELS[p.parentFinding.domain] || p.parentFinding.domain}
        </div>
        <div className="prediction-card-status">
          <span className={`finding-version-badge finding-version-badge-${tone}`}>
            {STATUS_LABELS[p.status] || p.status}
          </span>
          {isAntiPrediction && (
            <span className="prediction-anti-tag">Anti-prediction</span>
          )}
        </div>
      </div>

      <h2 className="prediction-card-claim">{p.label}</h2>

      {(p.band || p.central || p.falsifierBand) && (
        <div className="prediction-card-bands">
          {p.central != null && (
            <Band label="Central" value={p.central} tone="brass" />
          )}
          {p.band && (
            <Band label="Predicted band" value={`[${p.band[0]}, ${p.band[1]}]`} tone="sage" />
          )}
          {p.falsifierBand && (
            <Band label="Falsifier band" value={`[${p.falsifierBand[0]}, ${p.falsifierBand[1]}]`} tone="oxblood" />
          )}
        </div>
      )}

      {p.note && <p className="prediction-card-note">{p.note}</p>}

      <div className="prediction-card-foot">
        <div className="prediction-card-resolution">
          <div className="prediction-card-foot-eyebrow">Resolution</div>
          <div className="prediction-card-foot-value">
            {p.resolutionDate}
            {days != null && days > 0 && (
              <span className="prediction-card-countdown">
                &middot; {formatCountdown(days)}
              </span>
            )}
            {days != null && days <= 0 && (
              <span className="prediction-card-countdown prediction-card-countdown-due">
                &middot; due
              </span>
            )}
          </div>
        </div>
        <Link href={p.parentFinding.path} className="prediction-card-parent">
          <div className="prediction-card-foot-eyebrow">Parent finding</div>
          <div className="prediction-card-foot-value">
            {p.parentFinding.shortTitle || p.parentFinding.title}
            <span className="prediction-card-foot-arrow">→</span>
          </div>
        </Link>
      </div>

      <div className="prediction-card-handle">
        <code>{p.parentFinding.citationHandle}</code>
      </div>
    </article>
  );
}

function Band({ label, value, tone }) {
  return (
    <div className={`prediction-band prediction-band-${tone}`}>
      <div className="prediction-band-label">{label}</div>
      <div className="prediction-band-value">{value}</div>
    </div>
  );
}

function formatCountdown(days) {
  if (days < 30) return `${days} days`;
  if (days < 365) {
    const months = Math.floor(days / 30);
    return `~${months} month${months === 1 ? '' : 's'}`;
  }
  const years = Math.floor(days / 365);
  const rem = Math.floor((days % 365) / 30);
  if (rem === 0) return `~${years} year${years === 1 ? '' : 's'}`;
  return `~${years}y ${rem}mo`;
}
